import {
  ApiError,
  CancelError,
  CancelablePromise,
  type ChatListDto,
  type ChatListOrder,
  OfChatMessagesService,
  OnlyFansChatDto,
  type OnlyFansList,
  type OnlyFansMessage,
} from '@api'
import {
  attach,
  combine,
  createEffect,
  createEvent,
  createStore,
} from 'effector'
import { useUnit } from 'effector-react'
import _ from 'lodash'
import pick from 'lodash/pick'

import {
  CHATS_PER_PAGE,
  DEFAULT_SELECTED_LIST,
  UNREAD_LIST,
  filterOptions,
  orderOptions,
} from '@/consts/chats.ts'
import { $creatorId } from '@/models/creators.ts'
import { toast } from '@/models/toasts.ts'
import type { Chat, ChatItem } from '@/modules/chatting/types.ts'
import { prepareChat } from '@/utils/chats.ts'
import { handleError } from '@/utils/error'
import { isTruthyAndNotEmpty } from '@/utils/isTruthy.ts'

const noMessages: unknown[] = []

export const setSelectedList = createEvent<string>(
  'set selected list for chats',
)

export const $selectedList = createStore(DEFAULT_SELECTED_LIST.id, {
  name: 'selected list for chats',
}).on(setSelectedList, (_, listId) => listId)

export const setChatQuery = createEvent<string>('set chat query')
export const setQuery = _.debounce(setChatQuery, 500)
export const $chatQuery = createStore('', {
  name: 'chat query',
}).on(setChatQuery, (_, query) => query)

export const setChatFilter = createEvent<string>('set chat filter')
export const $chatFilter = createStore(filterOptions[0].value, {
  name: 'chat filter',
}).on(setChatFilter, (_, query) => query)

export const setChatOrder = createEvent<ChatListOrder>('set chat order')
export const $chatOrder = createStore<ChatListOrder>(orderOptions[0].value, {
  name: 'chat filter',
}).on(setChatOrder, (_, order) => order)

export const markCurrentChatAsRead = createEvent('mark current chat as read')
export const markChatAsReadFx = attach({
  name: 'mark chat as read',
  source: {
    creatorId: $creatorId,
  },
  mapParams: (fanId: number, { creatorId }) => ({
    creatorId: creatorId!,
    userId: fanId,
  }),
  effect: createEffect(OfChatMessagesService.markChatAsRead),
})

markChatAsReadFx.failData.watch((error) => {
  handleError(error, 'Something went wrong while marking chat as read')
})

export const $markingInProgress = createStore<Record<number, boolean>>(
  {},
  {
    name: 'list of chats being marked as read right now',
  },
)
  .on(markChatAsReadFx, (store, fanId) => ({ ...store, [fanId]: true }))
  .on(
    [markChatAsReadFx.done, markChatAsReadFx.fail],
    (store, { params: fanId }) => ({
      ...store,
      [fanId]: false,
    }),
  )

export const markAllChatsAsReadFx = attach({
  name: 'mark ALL chats as read',
  source: {
    creatorId: $creatorId,
  },
  mapParams: (_: void, { creatorId }) => ({
    creatorId: creatorId!,
  }),
  effect: createEffect(OfChatMessagesService.markAllAsRead),
})

markAllChatsAsReadFx.failData.watch((error) => {
  handleError(error, 'Something went wrong while marking all chats as read')
})

export const markChatAsUNReadFx = attach({
  name: 'mark chat as UNread',
  source: {
    creatorId: $creatorId,
  },
  mapParams: (fanId: number, { creatorId }) => ({
    creatorId: creatorId!,
    userId: fanId,
  }),
  effect: createEffect(OfChatMessagesService.markChatAsUnread),
})

markChatAsUNReadFx.failData.watch((error) => {
  handleError(error, 'Something went wrong while marking chat as unread')
})

/**
 * Get chat details. Used when we open new chat with fan.
 */
export const getChatsDetailsFx = attach({
  name: "get fan's chat details",
  source: {
    creatorId: $creatorId,
  },
  mapParams: (
    {
      userId,
    }: {
      /**
       * Param for effector, we don't need to mark messages as read in that case.
       */
      isMissing: boolean
      userId: number
    },
    { creatorId },
  ) => ({
    userId,
    creatorId: creatorId!,
  }),
  effect: createEffect(OfChatMessagesService.getChat),
})

getChatsDetailsFx.fail.watch(({ error, params }) => {
  handleError(error, 'Something went wrong while getting chat details', () =>
    getChatsDetailsFx(params),
  )
})

export interface ChatListRequest {
  creatorId: number
  page: number
  order?: ChatListOrder
  listFilter?: string
  filter?: string
  query?: string
}

let getChatsListRequest: CancelablePromise<ChatListDto> | null = null

export const getChatsFx = attach({
  name: "get fan's chats",
  source: {
    query: $chatQuery,
    filter: $chatFilter,
    order: $chatOrder,
    creatorId: $creatorId,
    selectedList: $selectedList,
  },
  mapParams: (
    params: Omit<
      ChatListRequest,
      'creatorId' | 'order' | 'listFilter' | 'filter' | 'query'
    >,
    { creatorId, query, filter, selectedList, order },
  ) => ({
    ...params,
    order,
    query,
    filter: selectedList === UNREAD_LIST.id ? UNREAD_LIST.id : filter,
    listFilter: [UNREAD_LIST.id, DEFAULT_SELECTED_LIST.id].includes(
      selectedList,
    )
      ? undefined
      : selectedList,
    creatorId: creatorId!,
    pageSize: CHATS_PER_PAGE,
  }),
  effect: createEffect(
    (...params: Parameters<typeof OfChatMessagesService.getChats>) => {
      getChatsListRequest = OfChatMessagesService.getChats(...params)
      getChatsListRequest.then(() => {
        getChatsListRequest = null
      })

      return getChatsListRequest
    },
  ),
})

export const cancelGetChatsListRequest = () => {
  if (!getChatsListRequest) return

  getChatsListRequest.cancel()
}

export const addChat = createEvent<OnlyFansChatDto>('add chat')

export const loadChats = (
  params: Omit<
    ChatListRequest,
    'creatorId' | 'order' | 'listFilter' | 'filter' | 'query'
  >,
) => {
  getChatsFx(params).catch(() => {
    // Errors are handled by getChatsFx.fail.watch
  })
}

getChatsFx.fail.watch(({ error, params }) => {
  if (error instanceof CancelError) {
    // do nothing
    return
  }
  if (error instanceof ApiError && error.status === 410) {
    resetChats()
    toast.warning('Chat list has been outdated. Refreshing...')

    return
  }

  handleError(error, 'Error fetching chats', () => loadChats(params))
})

export const $chatListError = createStore<Nullable<Error>>(null, {
  name: 'chat list error',
})
  .on(getChatsFx.failData, (_, error) => {
    if (error instanceof CancelError) {
      return null
    }

    return error
  })
  .reset(getChatsFx)

export const resetChats = createEvent<unknown>('reset chats')
export const $chatsPages = createStore<ChatListDto[]>(
  noMessages as ChatListDto[],
  {
    name: 'chat list pages',
  },
).reset(resetChats)

export const $allowedFanNumbering = $chatsPages.map((pages) => {
  const lastPage = pages[pages.length - 1]

  return lastPage?.alloweFanNumbering ?? []
})

export const $canLoadNextChatPages = combine(
  $chatsPages,
  getChatsFx.pending,
  $chatListError,
  (pages, isLoading, error) => {
    const lastPage = pages[pages.length - 1]

    return (lastPage ? lastPage.hasMore : true) && !isLoading && !error
  },
)

export const getNextChatsPageFx = attach({
  name: "get fan's chats' next page",
  source: {
    pages: $chatsPages,
  },
  mapParams: (_: void, { pages }) => ({
    page: pages.length,
  }),
  effect: getChatsFx,
})

export const loadNextChatsPage = () => {
  getNextChatsPageFx().catch(() => {
    // Errors are handled by getNextChatsPageFx.fail.watch
  })
}

getNextChatsPageFx.fail.watch(({ error, params }) => {
  handleError(error, 'Something went wrong while getting next chats page', () =>
    loadNextChatsPage(),
  )
})

export const updateFanName = createEvent<{ fanId: number; name: string }>(
  "update fan's name",
)
export const updateFanTimezone = createEvent<{
  fanId: number
  timeZone: Nullable<string>
}>("set fan's timezone")
export const updateFanCountryCode = createEvent<{
  fanId: number
  country: Nullable<string>
}>("set fan's country code")
export const updateFanNotice = createEvent<{ fanId: number; notice: string }>(
  "update fan's notice",
)
export const updateFanTranslationLanguage = createEvent<{
  fanId: number
  translationLanguage: string
}>("update fan's translation language")
export const updateFanOriginId = createEvent<{
  fanId: number
  originId: number
}>("update fan's origin id")
export const updateFanBirthday = createEvent<{
  fanId: number
  birthday: string
}>("update fan's birthday")
export const updateLists = createEvent<{
  fanId: number
  add: OnlyFansList[]
  remove: OnlyFansList[]
}>("update fan's lists")
export const updateLastMessage = createEvent<{
  fanId: number
  lastMessage: OnlyFansMessage
  pushToTheTopIfChanged?: boolean
}>("update fan's last message")
export const toggleFollow = createEvent<{
  fanId: number
  subscribedBy: boolean
  canSendMessage: boolean
  canNotSendReason: string
}>("toggle fan's follow")
export const toggleMute = createEvent<{
  fanId: number
  isMutedNotifications: boolean
}>("toggle fan's mute")
export const toggleRestrict = createEvent<{
  fanId: number
  canSendMessage: boolean
}>("toggle fan's restrict")
export const togglePinMessage = createEvent<{
  fanId: number
  pinned: boolean
}>("toggle fan's message pin")
export const deleteChat = createEvent<number>('delete chat by id')

/**
 * Map for all chats, even if they shouldn't be rendered in sidebar.
 */
export const $chatsMap = createStore<Record<number, Chat>>(
  {},
  { name: 'chats map by id' },
)
  .on(getChatsFx.doneData, (store, { chats }) => {
    return chats.reduce(
      (result, { lastMessage, ...chat }) => ({
        ...result,
        [chat.withUser.id]: {
          ...chat,
          lastMessageId: lastMessage?.id,
        },
      }),
      store,
    )
  })
  .on(getChatsDetailsFx.doneData, (store, { lastMessage, ...chat }) => {
    return {
      ...store,
      [chat.withUser.id]: {
        ...chat,
        lastMessageId: lastMessage?.id,
      },
    }
  })
  .on(updateFanName, (store, { fanId, name }) => {
    const oldChat = store[fanId]

    return oldChat
      ? {
          ...store,
          [fanId]: {
            ...oldChat,
            withUser: {
              ...oldChat.withUser,
              displayName: name,
            },
          },
        }
      : store
  })
  // @ts-ignore
  .on(updateFanTimezone, (store, { fanId, timeZone }) => {
    const oldChat = store[fanId]

    return oldChat
      ? {
          ...store,
          [fanId]: {
            ...oldChat,
            withUser: {
              ...oldChat.withUser,
              timeZone,
            },
          },
        }
      : store
  })
  // @ts-ignore
  .on(updateFanCountryCode, (store, { fanId, country }) => {
    const oldChat = store[fanId]

    return oldChat
      ? {
          ...store,
          [fanId]: {
            ...oldChat,
            withUser: {
              ...oldChat.withUser,
              country,
            },
          },
        }
      : store
  })
  .on(updateFanNotice, (store, { fanId, notice }) => {
    const oldChat = store[fanId]

    return oldChat
      ? {
          ...store,
          [fanId]: {
            ...oldChat,
            withUser: {
              ...oldChat.withUser,
              notice,
            },
          },
        }
      : store
  })
  .on(updateFanTranslationLanguage, (store, { fanId, translationLanguage }) => {
    const oldChat = store[fanId]

    return oldChat
      ? {
          ...store,
          [fanId]: {
            ...oldChat,
            withUser: {
              ...oldChat.withUser,
              translationLanguage,
            },
          },
        }
      : store
  })
  .on(updateFanOriginId, (store, { fanId, originId }) => {
    const oldChat = store[fanId]

    return oldChat
      ? {
          ...store,
          [fanId]: {
            ...oldChat,
            withUser: {
              ...oldChat.withUser,
              originId,
            },
          },
        }
      : store
  })
  .on(updateFanBirthday, (store, { fanId, birthday }) => {
    const oldChat = store[fanId]

    return oldChat
      ? {
          ...store,
          [fanId]: {
            ...oldChat,
            withUser: {
              ...oldChat.withUser,
              birthday,
            },
          },
        }
      : store
  })
  .on(updateLists, (store, { fanId, add, remove }) => {
    const oldChat = store[fanId]
    const addedLists = add.map((list) => ({
      ...pick(list, ['id', 'name', 'type']),
      hasUser: true,
    }))
    const newLists = oldChat.withUser.listsStates
      ?.filter(({ id }) => !remove.some((list) => list.id === id))
      .concat(addedLists)

    return oldChat
      ? {
          ...store,
          [fanId]: {
            ...oldChat,
            withUser: {
              ...oldChat.withUser,
              listsStates: newLists,
            },
          },
        }
      : store
  })
  .on(updateLastMessage, (store, { fanId, lastMessage }) => {
    const oldChat = store[fanId]

    return oldChat
      ? {
          ...store,
          [fanId]: {
            ...oldChat,
            lastMessageId: lastMessage?.id,
          },
        }
      : store
  })
  .on(
    toggleFollow,
    (store, { fanId, canSendMessage, canNotSendReason, subscribedBy }) => {
      const oldChat = store[fanId]

      return oldChat
        ? {
            ...store,
            [fanId]: {
              ...oldChat,
              canSendMessage,
              canNotSendReason,
              withUser: {
                ...oldChat.withUser,
                subscribedBy,
              },
            },
          }
        : store
    },
  )
  .on(toggleMute, (store, { fanId, isMutedNotifications }) => {
    const oldChat = store[fanId]

    return oldChat
      ? {
          ...store,
          [fanId]: {
            ...oldChat,
            isMutedNotifications,
          },
        }
      : store
  })
  .on(toggleRestrict, (store, { fanId, canSendMessage }) => {
    const oldChat = store[fanId]

    return oldChat
      ? {
          ...store,
          [fanId]: {
            ...oldChat,
            canSendMessage,
            canNotSendReason: canSendMessage ? '' : 'restricted',
          },
        }
      : store
  })
  .on(togglePinMessage, (store, { pinned, fanId }) => {
    const oldChat = store[fanId]

    return oldChat
      ? {
          ...store,
          [fanId]: {
            ...oldChat,
            countPinnedMessages: pinned
              ? oldChat.countPinnedMessages + 1
              : oldChat.countPinnedMessages - 1,
          },
        }
      : store
  })
  .on(addChat, (store, chat) => {
    return {
      ...store,
      [chat.withUser.id]: chat,
    }
  })

/**
 * List of visible chats.
 */
export const $chats = createStore<ChatItem[]>(noMessages as ChatItem[], {
  name: "fan's chats",
})
  .on(addChat, (store, chat) => {
    return store.concat(prepareChat(chat, store.length))
  })
  .on(
    updateLastMessage,
    (store, { fanId, lastMessage, pushToTheTopIfChanged }) => {
      const moveToTheTop =
        pushToTheTopIfChanged &&
        store.some(
          (chat) => chat.id === fanId && chat.lastMessageId !== lastMessage.id,
        )

      if (moveToTheTop) {
        const oldChat = store.find((chat) => chat.id === fanId)!

        return [
          {
            ...oldChat,
            lastMessageId: lastMessage.id,
            receiveDate: Date.now(),
            order: store[0] ? store[0]?.order - 1 : 0,
          },
          ...store.filter((chat) => chat.id !== fanId),
        ]
      } else {
        return store.map((chat) =>
          chat.id === fanId
            ? {
                ...chat,
                lastMessageId: lastMessage.id,
              }
            : chat,
        )
      }
    },
  )
  .on(getChatsFx.doneData, (store, { chats }) => {
    const allChats = store.concat(
      chats
        .filter(isTruthyAndNotEmpty)
        .filter(({ withUser }) => !withUser.isBlocked)
        .map((chat, index) => prepareChat(chat, store.length + index)),
    )

    return Object.values(
      allChats.reduce<Record<number, ChatItem>>((result, newChat) => {
        const oldChat = result[newChat.id]

        result[newChat.id] = oldChat
          ? {
              ...newChat,
              order: oldChat.order,
            }
          : newChat

        return result
      }, {}),
    ).sort((a, b) => a.order - b.order)
  })
  .on(deleteChat, (store, chatId) => store.filter(({ id }) => id !== chatId))
  .reset(resetChats)

export const setFanId = createEvent<Nullable<number>>('set fan id')
/**
 * Id of opened chat (fan chat).
 */
export const $fanId = createStore<number>(0).on(setFanId, (_, id) =>
  id ? Math.max(id, 0) : 0,
)
export const $activeChatDetails = combine($fanId, $chatsMap, (fanId, map) => {
  return map[fanId] ?? null
})

export const useChatDetails = (chatId: number) => {
  const chatsMap = useUnit($chatsMap)

  return chatsMap[chatId] ?? null
}

export const $fan = $activeChatDetails.map((chat) => chat?.withUser ?? null)
export const $fanOrigin = $fan.map((fan) => fan?.originId ?? null)
export const $fanBirthday = $fan.map((fan) => fan?.birthday ?? null)
export const $fanTimezone = $fan.map((fan) => fan?.timeZone ?? null)
export const $fanCountryCode = $fan.map((fan) => fan?.country ?? null)

export const scrollChatToBottomFx = createEffect({
  name: 'scroll chat to the bottom',
  handler: (smooth: boolean) => {
    setTimeout(() => {
      const messagesContainer = document.querySelector(
        '#chatting-messages-container',
      )

      if (messagesContainer) {
        messagesContainer.scrollTo({
          left: 0,
          top: messagesContainer.scrollHeight,
          behavior: smooth ? 'smooth' : 'instant',
        })
      }
    }, 1)
  },
})
